import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Alert,
  App,
} from 'antd';
import { GeoRule, GeoRuleInput, RuleScope } from './types';
import { useCreateRuleMutation, useUpdateRuleMutation } from './queries';
import { ApiError } from '../../api/types';

interface RuleFormModalProps {
  open: boolean;
  rule: GeoRule | null;
  onClose: () => void;
}

interface FormValues {
  name: string;
  scope: RuleScope;
  value: string;
  action: 'allow' | 'deny';
  priority: number;
  statusSwitch: boolean;
}

const SCOPE_PLACEHOLDERS: Record<RuleScope, string> = {
  ip: '203.0.113.10',
  cidr: '198.51.100.0/24',
  account: '10042',
  country: 'US',
};

const SCOPE_HELP_TEXT: Record<RuleScope, string> = {
  ip: 'Enter a valid IPv4 or IPv6 address (e.g. 203.0.113.10)',
  cidr: 'Enter a CIDR notation range (e.g. 198.51.100.0/24)',
  account: 'Enter a positive numeric account ID (e.g. 10042)',
  country: 'Enter a 2-letter uppercase ISO country code (e.g. US)',
};

function isValidIp(val: string): boolean {
  const ipv4Regex =
    /^(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
  if (ipv4Regex.test(val)) {
    return true;
  }
  const ipv6Regex =
    /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]+|::(ffff(:0{1,4})?:)?((25[0-5]|(2[0-4]|1?[0-9])?[0-9])\.){3}(25[0-5]|(2[0-4]|1?[0-9])?[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1?[0-9])?[0-9])\.){3}(25[0-5]|(2[0-4]|1?[0-9])?[0-9]))$/;
  return ipv6Regex.test(val);
}

function isValidCidr(val: string): boolean {
  const parts = val.split('/');
  if (parts.length !== 2) {
    return false;
  }
  const [ip, prefixStr] = parts;
  if (!/^\d+$/.test(prefixStr)) {
    return false;
  }
  const prefix = parseInt(prefixStr, 10);
  const ipv4Regex =
    /^(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
  if (ipv4Regex.test(ip)) {
    return prefix >= 0 && prefix <= 32;
  }
  if (isValidIp(ip)) {
    return prefix >= 0 && prefix <= 128;
  }
  return false;
}

export const RuleFormModal: React.FC<RuleFormModalProps> = ({
  open,
  rule,
  onClose,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [selectedScope, setSelectedScope] = useState<RuleScope>('ip');
  const [modalError, setModalError] = useState<string | null>(null);

  const createMutation = useCreateRuleMutation();
  const updateMutation = useUpdateRuleMutation();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (open) {
      setModalError(null);
      if (rule) {
        setSelectedScope(rule.scope);
        form.setFieldsValue({
          name: rule.name,
          scope: rule.scope,
          value: rule.value,
          action: rule.action,
          priority: rule.priority,
          statusSwitch: rule.status === 'active',
        });
      } else {
        setSelectedScope('ip');
        form.resetFields();
        form.setFieldsValue({
          scope: 'ip',
          action: 'allow',
          priority: 0,
          statusSwitch: true,
        });
      }
    }
  }, [open, rule, form]);

  const handleScopeChange = (newScope: RuleScope) => {
    setSelectedScope(newScope);
    form.setFieldsValue({ value: '' });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setModalError(null);
      const payload: GeoRuleInput = {
        name: values.name.trim(),
        scope: values.scope,
        value: values.value.trim(),
        action: values.action,
        priority: values.priority,
        status: values.statusSwitch ? 'active' : 'inactive',
      };

      if (rule) {
        await updateMutation.mutateAsync({ id: rule.id, input: payload });
        message.success('Rule updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        message.success('Rule created successfully');
      }
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) {
        // Antd form validation error, do not treat as API error
        return;
      }
      if (err instanceof ApiError) {
        const errorMsg = err.code ? `[${err.code}] ${err.message}` : err.message;
        setModalError(errorMsg);
      } else if (err instanceof Error) {
        setModalError(err.message);
      } else {
        setModalError('Failed to save rule. Please try again.');
      }
    }
  };

  return (
    <Modal
      title={rule ? 'Edit geo rule' : 'Create geo rule'}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText={rule ? 'Save changes' : 'Create'}
      confirmLoading={isSubmitting}
      cancelButtonProps={{ disabled: isSubmitting }}
      destroyOnHidden
    >
      {modalError && (
        <Alert
          message={modalError}
          type="error"
          showIcon
          className="mb-4"
          closable
          onClose={() => setModalError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{
          scope: 'ip',
          action: 'allow',
          priority: 0,
          statusSwitch: true,
        }}
      >
        <Form.Item
          name="name"
          label="Rule Name"
          rules={[
            { required: true, message: 'Please enter a rule name' },
            {
              validator: (_, value) => {
                if (value && value.trim().length === 0) {
                  return Promise.reject(new Error('Rule name cannot be only whitespace'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="e.g. Block suspicious subnet" disabled={isSubmitting} />
        </Form.Item>

        <Form.Item
          name="scope"
          label="Scope"
          rules={[{ required: true, message: 'Please select a scope' }]}
        >
          <Select
            onChange={handleScopeChange}
            disabled={isSubmitting}
            options={[
              { value: 'ip', label: 'IP' },
              { value: 'cidr', label: 'CIDR' },
              { value: 'account', label: 'Account' },
              { value: 'country', label: 'Country' },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="value"
          label="Value"
          extra={SCOPE_HELP_TEXT[selectedScope]}
          normalize={(val) => (selectedScope === 'country' ? val?.toUpperCase() : val)}
          rules={[
            { required: true, message: 'Please enter a value' },
            {
              validator: (_, value) => {
                if (!value || !value.trim()) {
                  return Promise.reject(new Error('Value is required'));
                }
                const trimmed = value.trim();
                if (selectedScope === 'ip') {
                  if (!isValidIp(trimmed)) {
                    return Promise.reject(
                      new Error('Please enter a valid IPv4 or IPv6 address (e.g. 203.0.113.10)')
                    );
                  }
                } else if (selectedScope === 'cidr') {
                  if (!isValidCidr(trimmed)) {
                    return Promise.reject(
                      new Error('Please enter a valid CIDR notation (e.g. 198.51.100.0/24)')
                    );
                  }
                } else if (selectedScope === 'country') {
                  if (!/^[A-Z]{2}$/.test(trimmed)) {
                    return Promise.reject(
                      new Error('Country code must be 2 uppercase letters (e.g. US)')
                    );
                  }
                } else if (selectedScope === 'account') {
                  if (!/^[1-9]\d*$/.test(trimmed)) {
                    return Promise.reject(
                      new Error('Account ID must be a positive integer')
                    );
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder={SCOPE_PLACEHOLDERS[selectedScope]}
            disabled={isSubmitting}
          />
        </Form.Item>

        <Form.Item
          name="action"
          label="Action"
          rules={[{ required: true, message: 'Please select an action' }]}
        >
          <Select
            disabled={isSubmitting}
            options={[
              { value: 'allow', label: 'Allow' },
              { value: 'deny', label: 'Deny' },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="priority"
          label="Priority"
          rules={[
            { required: true, message: 'Please specify priority' },
            {
              type: 'integer',
              min: 0,
              message: 'Priority must be an integer >= 0',
            },
          ]}
        >
          <InputNumber
            min={0}
            precision={0}
            className="w-full"
            disabled={isSubmitting}
          />
        </Form.Item>

        <Form.Item
          name="statusSwitch"
          label="Status"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            disabled={isSubmitting}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
